import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  effect,
  ElementRef,
  inject,
  input,
  OnInit,
  output,
  signal,
  untracked,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { FormBuilder, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';

import { OrderService, ScriptLoaderService, TrimmedValueDirective, UserService } from '@core';
import { ThemeService } from '@shared';
import { combineLatest, filter, Observable, startWith, switchMap, takeUntil, tap } from 'rxjs';
import {
  getMicroformStyles,
  MICROFORM_URL,
  microformMonthOptions,
  microformYearOptions,
  MONTH_LABELS,
} from './constants';

declare const Flex: any; // Declare the Flex library

interface ICheckoutPaymentOptionsForm {
  firstName: FormControl<string>;
  lastName: FormControl<string>;
  expMonth: FormControl<string>;
  expYear: FormControl<string>;
}

@Component({
  selector: 'app-checkout-payment-options-form',
  templateUrl: './checkout-payment-options-form.component.html',
  styleUrls: ['./checkout-payment-options-form.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ReactiveFormsModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatAutocompleteModule,
    TrimmedValueDirective,
  ],
})
export class CheckoutPaymentOptionsFormComponent implements OnInit {
  readonly isSubmitTouched = input(false);
  cardType = output<string>();
  onNameChange = output<{ firstName: string; lastName: string }>();

  private readonly userService = inject(UserService);
  private readonly fb = inject(FormBuilder);
  private readonly destroyRef = inject(DestroyRef);
  private readonly orderService = inject(OrderService);
  private readonly scriptLoaderService = inject(ScriptLoaderService);
  private readonly themeService = inject(ThemeService);
  private readonly el = inject(ElementRef);

  numberContainer = viewChild<ElementRef>('numberContainer');
  securityCodeContainer = viewChild<ElementRef>('securityCodeContainer');

  readonly isNumberFieldValid = signal(false);
  readonly isSecurityCodeFieldValid = signal(false);

  private flexInstance: any;
  private microform: any;
  private initializedToken: string | null = null;
  private appliedDarkTheme: boolean | null = null;
  private fieldsDisabled = false;
  private isCardNumberFocusPending = false;

  numberField: any;
  securityCodeField: any;

  validSecurityCodeProvided$ = toObservable(this.isSecurityCodeFieldValid);
  validCardNumber$ = toObservable(this.isNumberFieldValid);

  readonly monthOptions = microformMonthOptions;
  readonly yearOptions = microformYearOptions;
  readonly labels = {
    cardName: 'Enter your name on card',
    firstName: 'Enter your first name',
    lastName: 'Enter your last name',
    cardNumber: 'Enter your card number',
    securityNumber: 'Enter security code on your card',
  };

  readonly form: FormGroup<ICheckoutPaymentOptionsForm> = this.fb.group({
    firstName: this.fb.control(null, [Validators.required]),
    lastName: this.fb.control(null, [Validators.required]),
    expMonth: this.fb.control(null, [Validators.required]),
    expYear: this.fb.control(null, [Validators.required]),
  });

  get firstNameControl(): FormControl<string> {
    return this.form.controls.firstName;
  }

  get lastNameControl(): FormControl<string> {
    return this.form.controls.lastName;
  }

  get expMonthControl(): FormControl<string> {
    return this.form.controls.expMonth;
  }

  get expYearControl(): FormControl<string> {
    return this.form.controls.expYear;
  }

  constructor() {
    // The casino profile may arrive after the form is created (e.g. a page reload at /checkout),
    // so prefill the name whenever it becomes known instead of reading it once on init.
    effect(() => {
      const firstName = this.userService.firstName();
      const lastName = this.userService.lastName();
      untracked(() => this.prefillName(firstName, lastName));
    });

    effect(() => {
      const captureContextToken = this.orderService.captureContextToken();
      const isFlexScriptLoaded = this.scriptLoaderService.isLoaded(MICROFORM_URL);
      const isDarkTheme = this.themeService.isDark();
      const numberContainer = this.numberContainer();
      const securityCodeContainer = this.securityCodeContainer();

      if (!captureContextToken) {
        console.warn(
          'Can`t setup microform instance for payment processing. Capture context token from backend is absent',
        );
        return;
      }

      // Wait for the Flex script: on a full page reload at /checkout the capture token
      // can arrive before the CyberSource script finishes loading (RID-979)
      if (!isFlexScriptLoaded) return;

      // Wait for the field containers to render (viewChild signals)
      if (!numberContainer || !securityCodeContainer) return;

      // First setup or capture-token change → (re)create the Flex iframes. The
      // iframes are isolated from the app stylesheets, so they're created with the
      // theme-matching styles (RID-979).
      if (captureContextToken !== this.initializedToken) {
        this.initializedToken = captureContextToken;
        this.appliedDarkTheme = isDarkTheme;
        this.setupMicroform(captureContextToken, isDarkTheme);
        return;
      }

      // Token unchanged but Light/Dark toggled → restyle the existing fields in
      // place instead of recreating them, so the entered values and the disabled
      // (order-review) state survive a theme switch (RID-1035). Recreating would
      // reset the fields to an empty, editable state.
      if (isDarkTheme !== this.appliedDarkTheme) {
        this.appliedDarkTheme = isDarkTheme;
        this.updateMicroformStyles(isDarkTheme);
      }
    });
  }

  ngOnInit(): void {
    this.bindExpirationCardDataChangeToCreatingMicroformToken();
    this.handleFormChanges();
  }

  private prefillName(firstName: string, lastName: string): void {
    // Prefill only what the casino profile has and never overwrite what the player typed;
    // an empty name is left for the player to enter.
    const { firstName: firstNameControl, lastName: lastNameControl } = this.form.controls;
    if (firstName && !firstNameControl.value && !firstNameControl.dirty) {
      firstNameControl.setValue(firstName);
    }
    if (lastName && !lastNameControl.value && !lastNameControl.dirty) {
      lastNameControl.setValue(lastName);
    }
  }

  private handleFormChanges(): void {
    this.form.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((value) => {
      this.onNameChange.emit({ firstName: value.firstName, lastName: value.lastName });
    });
  }

  focusOnInvalidField(): void {
    if (!this.isFormInvalid()) return;
    setTimeout(() => {
      const field = this.el.nativeElement.querySelector(
        '.flex-microform.invalid, .ng-invalid:not(form):not([disabled])',
      ) as HTMLElement;

      if (!field) return;
      const input = field.querySelector<HTMLElement>('iframe, input');
      if (input) {
        input?.focus();
        input?.click();
        return;
      }
      field?.focus();
    });
  }

  optionYearAriaLabel(option: number | string): string {
    return `Select ${option} year`;
  }

  optionMonthAriaLabel(option: number | string) {
    const label = MONTH_LABELS[Number(option) - 1] || option;
    return `Select ${label} month`;
  }

  private setupMicroform(captureToken: string, isDark: boolean) {
    this.initiateFlex(captureToken, isDark);
  }

  // Re-apply theme-matching styles to the already-loaded fields without
  // recreating them — preserves the entered values and validity. `disabled` is
  // re-sent alongside `styles` because update() replaces the field options.
  private updateMicroformStyles(isDark: boolean): void {
    const styles = getMicroformStyles(isDark);
    this.numberField?.update({ styles, disabled: this.fieldsDisabled });
    this.securityCodeField?.update({ styles, disabled: this.fieldsDisabled });
  }

  bindExpirationCardDataChangeToCreatingMicroformToken() {
    const { valueChanges: expMonth$, value: initExpMonth } = this.form.controls.expMonth;
    const expMonthChanges = expMonth$.pipe(startWith(initExpMonth));

    const { valueChanges: expYear$, value: initExpYear } = this.form.controls.expYear;
    const expYearChanges = expYear$.pipe(startWith(initExpYear));

    combineLatest([expMonthChanges, expYearChanges, this.validCardNumber$, this.validSecurityCodeProvided$])
      .pipe(
        filter(([expirationMonth, expirationYear, validCard]) => !!expirationMonth && !!expirationYear && !!validCard),
        switchMap(([expirationMonth, expirationYear]) =>
          this.getTransactionTokenRequest(expirationMonth, expirationYear),
        ),
        tap((transactionToken) => this.orderService.setTransientToken(transactionToken)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  private getTransactionTokenRequest(expirationMonth: string, expirationYear: string): Observable<string> {
    return new Observable<string | null>((observer) => {
      return this.microform.createToken(
        { expirationMonth: expirationMonth, expirationYear: +expirationYear },
        (_: unknown, transactionToken: string) => {
          observer.next(transactionToken);
          observer.complete();
        },
      );
    });
  }

  isFormInvalid(): boolean {
    return this.form.invalid || !this.isSecurityCodeFieldValid() || !this.isNumberFieldValid();
  }

  private initiateFlex(captureContext: string, isDark: boolean): void {
    // Drop previously loaded iframes (re-init on theme/token change) and reset
    // validity — recreated fields start empty
    this.numberContainer().nativeElement.replaceChildren();
    this.securityCodeContainer().nativeElement.replaceChildren();
    this.isNumberFieldValid.set(false);
    this.isSecurityCodeFieldValid.set(false);

    this.flexInstance = new Flex(captureContext);
    this.microform = this.flexInstance.microform({
      styles: getMicroformStyles(isDark),
    });

    this.numberField = this.microform.createField('number', {
      placeholder: '0000-0000-0000-0000',
      'aria-label': this.labels.cardNumber,
      'aria-placeholder': this.labels.cardNumber,
    });
    this.securityCodeField = this.microform.createField('securityCode', {
      placeholder: '000',
      'aria-label': this.labels.securityNumber,
      'aria-placeholder': this.labels.securityNumber,
    });

    this.numberField.on('change', (data) => {
      const cardType = data.card?.[0]?.brandedName;
      if (cardType) {
        this.cardType.emit(cardType.toLowerCase());
      }

      this.isNumberFieldValid.set(data.valid);
    });

    this.securityCodeField.on('change', (data) => {
      this.isSecurityCodeFieldValid.set(data.valid);
    });

    this.numberField.on('load', () => {
      if (this.isCardNumberFocusPending) {
        this.isCardNumberFocusPending = false;
        this.numberField.focus();
      }
    });

    this.numberField.load(this.numberContainer().nativeElement);
    this.securityCodeField.load(this.securityCodeContainer().nativeElement);
  }

  disableFields(): void {
    this.fieldsDisabled = true;
    this.numberField.update({ disabled: true });
    this.securityCodeField.update({ disabled: true });
  }

  enableFields(): void {
    this.fieldsDisabled = false;
    this.numberField?.update({ disabled: false });
    this.securityCodeField?.update({ disabled: false });
  }

  // Focuses the card number once the (re)created Cybersource field has loaded.
  focusCardNumberOnLoad(): void {
    this.isCardNumberFocusPending = true;
  }
}
